import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { CheckCircle, ArrowBack, Star, Business, Security } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { 
  SubscriptionPlan, 
  getCorporatePlans, 
  getLawEnforcementPlans, 
  formatPrice, 
  getAnnualSavings,
} from '../../constants/Subscriptions';

const RegisterContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#07090e',
  backgroundImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(34, 211, 238, 0.08) 0%, transparent 50%)',
  padding: '20px',
}));

const RegisterPaper = styled(Paper)(({ theme }) => ({
  padding: '40px',
  borderRadius: '24px',
  maxWidth: '600px',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
}));

const LogoContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '30px',
});

const PlanCard = styled(Card)<{ selected?: boolean; disabled?: boolean }>(({ theme, selected, disabled }) => ({
  marginBottom: '20px',
  borderRadius: '16px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  border: selected ? `3px solid ${theme.palette.primary.main}` : `2px solid ${theme.palette.divider}`,
  backgroundColor: disabled ? theme.palette.action.disabledBackground : theme.palette.background.paper,
  opacity: disabled ? 0.6 : 1,
  transition: 'all 0.3s ease',
  '&:hover': {
    border: disabled ? `2px solid ${theme.palette.divider}` : `3px solid ${theme.palette.primary.light}`,
  },
}));

const NextButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '50px',
  backgroundColor: theme.palette.action.selected,
  color: theme.palette.text.secondary,
  fontSize: '16px',
  fontWeight: 'bold',
  marginBottom: '10px',
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    color: theme.palette.common.white,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '50px',
  backgroundColor: 'transparent',
  color: theme.palette.text.secondary,
  fontSize: '16px',
  fontWeight: 'bold',
  border: `2px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

interface Step1Data {
  companyName: string;
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface Step2Data {
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  phoneNumber: string;
}

type CombinedData = Step1Data & Step2Data;

const RegisterStep3: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  
  const combinedData = location.state as CombinedData;
  
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<'corporate' | 'law-enforcement'>('corporate');
  const [isAnnual, setIsAnnual] = useState(false);
  
  const currentPlans = selectedCategory === 'corporate' ? getCorporatePlans() : getLawEnforcementPlans();

  // Redirect to step 1 if no data from previous steps
  React.useEffect(() => {
    if (!combinedData) {
      navigate('/register');
    }
  }, [combinedData, navigate]);

  const handlePlanSelect = (plan: SubscriptionPlan) => {
    if (!plan.isAvailable) {
      return; // Don't allow selection of unavailable plans
    }
    setSelectedPlan(plan);
    setError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!selectedPlan) {
      setError('Please select a subscription plan');
      return;
    }

    // Combine all data including selected plan and billing interval (for Stripe Checkout)
    const finalData = {
      ...combinedData,
      plan: selectedPlan,
      isAnnual,
    };

    // Navigate to Summary step
    navigate('/register/summary', { state: finalData });
  };

  const handleBack = () => {
    navigate('/register/step2', { state: combinedData });
  };

  if (!combinedData) {
    return null; // Will redirect in useEffect
  }

  return (
    <RegisterContainer>
      <RegisterPaper elevation={8}>
        <form onSubmit={handleSubmit}>
          <LogoContainer>
            <Box
              component="img"
              src="/SkimBusterHeaderLogin-21.svg"
              alt="SkimBuster by MICCLO"
              sx={{
                height: '100px',
                objectFit: 'contain',
                filter: (theme) => theme.palette.mode === 'dark' ? 'invert(1) brightness(0.9)' : 'none'
              }}
            />
            <Typography
              variant="h5"
              sx={{
                marginTop: 2,
                fontWeight: 'bold',
                color: muiTheme.palette.text.primary,
              }}
            >
              Choose Your Plan
            </Typography>
            <Typography
              variant="body2"
              sx={{
                marginTop: 1,
                color: muiTheme.palette.text.secondary,
                textAlign: 'center',
              }}
            >
              Step 3 of 4
            </Typography>
          </LogoContainer>

          {/* Category Selection */}
          <Tabs
            value={selectedCategory}
            onChange={(_, newValue) => {
              setSelectedCategory(newValue);
              setSelectedPlan(null); // Reset selection when changing category
            }}
            centered
            sx={{ marginBottom: 3 }}
          >
            <Tab 
              value="corporate" 
              label="Corporate" 
              icon={<Business />} 
              iconPosition="start"
              sx={{ minWidth: 150 }}
            />
            <Tab 
              value="law-enforcement" 
              label="Government" 
              icon={<Security />} 
              iconPosition="start"
              sx={{ minWidth: 150 }}
            />
          </Tabs>

          {/* Annual Pricing Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isAnnual}
                  onChange={(e) => setIsAnnual(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  Annual Billing {isAnnual && '(Save up to 2 months!)'}
                </Typography>
              }
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ marginBottom: 2, borderRadius: '16px' }}>
              {error}
            </Alert>
          )}

          <Box sx={{ marginBottom: 3 }}>
            {currentPlans.map((plan) => {
              const savings = getAnnualSavings(plan);
              return (
                <PlanCard
                  key={plan.id}
                  selected={selectedPlan?.id === plan.id}
                  disabled={!plan.isAvailable}
                  onClick={() => handlePlanSelect(plan)}
                  elevation={selectedPlan?.id === plan.id ? 8 : 2}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: muiTheme.palette.text.primary }}>
                          {plan.title}
                          {plan.isPopular && (
                            <Chip
                              label="Popular"
                              size="small"
                              icon={<Star />}
                              sx={{
                                marginLeft: 1,
                                backgroundColor: muiTheme.palette.warning.main,
                                color: muiTheme.palette.warning.contrastText,
                              }}
                            />
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 1 }}>
                          {plan.description}
                        </Typography>
                        
                        {/* Plan Details */}
                        <Box sx={{ marginTop: 1 }}>
                          {(plan.maxSites ?? 0) > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Sites: {plan.maxSites}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block">
                            Users: {plan.maxUsers ?? 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            History: {plan.historyRetention ?? 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: muiTheme.palette.primary.main }}>
                          {formatPrice(plan, isAnnual)}
                        </Typography>
                        {isAnnual && savings > 0 && (
                          <Chip
                            label={`Save $${savings}`}
                            size="small"
                            color="success"
                            sx={{ marginTop: 0.5 }}
                          />
                        )}
                      </Box>
                    </Box>

                    <List dense>
                      {plan.features.map((feature, index) => (
                        <ListItem key={index} sx={{ paddingLeft: 0, paddingY: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 30 }}>
                            <CheckCircle 
                              sx={{ 
                                fontSize: 20, 
                                color: plan.isAvailable ? muiTheme.palette.success.main : muiTheme.palette.text.disabled 
                              }} 
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={feature} 
                            primaryTypographyProps={{
                              variant: 'body2',
                              color: plan.isAvailable ? 'text.primary' : 'text.disabled'
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    {!plan.isAvailable && (
                      <Box sx={{ marginTop: 2, textAlign: 'center' }}>
                        <Chip
                          label="Coming Soon"
                          variant="outlined"
                          sx={{
                            color: muiTheme.palette.text.disabled,
                            borderColor: muiTheme.palette.text.disabled,
                          }}
                        />
                      </Box>
                    )}
                  </CardContent>
                </PlanCard>
              );
            })}
          </Box>

          <NextButton 
            type="submit" 
            variant="contained"
            disabled={!selectedPlan}
          >
            Continue to Summary
          </NextButton>

          <BackButton
            type="button"
            variant="outlined"
            onClick={handleBack}
            startIcon={<ArrowBack />}
          >
            Back
          </BackButton>
        </form>
      </RegisterPaper>
    </RegisterContainer>
  );
};

export default RegisterStep3;